/**
 * Owner validation utilities for employee level checks.
 *
 * Validates whether a selected user falls within a restricted employee
 * level tier before allowing assignment as an application owner.
 *
 * Two-tier configuration:
 * - Exclude tier: blocks assignment with an informational message
 * - Warn tier: shows a confirmation dialog; user may proceed
 *
 * Fail-open: if the user is not found or the API call fails,
 * assignment proceeds without any dialog.
 */

import { ConfigHelper } from '@devxdiscover/devhub-common';

/**
 * Raw response from the obiee-data-reader employee level endpoint.
 */
export interface EmployeeLevelResponse {
  /** Network login ID (empty string when user not found) */
  networkId: string;
  /** Employee level string, e.g. "L1"–"L10" or "NULL" */
  empLevel: string;
}

/** Tier result returned by {@link checkOwnerEligibility}. */
export type OwnerEligibilityTier = 'exclude' | 'warn' | 'allow';

/** Result of an owner eligibility check. */
export interface OwnerEligibilityResult {
  /** The tier the selected user falls into. */
  tier: OwnerEligibilityTier;
  /** The raw employee level string, if available. */
  empLevel?: string;
}

/** Default exclude levels when env var is not set. */
const DEFAULT_EXCLUDE = 'NULL,L1,L2,L3';

/** Default warn levels when env var is not set. */
const DEFAULT_WARN = 'L4,L5';

/**
 * Parse a comma-delimited level string into an array.
 * Returns an empty array for empty/blank input or the sentinel value "none".
 *
 * Note: Backstage config rejects empty strings as invalid, so use `"none"`
 * as the env var value to disable a tier (e.g. `APP_OWNER_EMPLEVEL_EXCLUDE="none"`).
 * @param csv - Comma-delimited level string
 */
function parseLevels(csv: string | undefined): string[] {
  if (!csv || csv.trim() === '' || csv.trim().toLowerCase() === 'none') {
    return [];
  }
  return csv
    .split(',')
    .map(s => s.trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Read exclude levels from app config, falling back to defaults.
 * Backed by env var `APP_OWNER_EMPLEVEL_EXCLUDE` via `app-config.yaml`.
 *
 * Set env var to `"none"` to disable this tier. Do not use empty string
 * — Backstage rejects empty strings and will throw at runtime.
 */
function getExcludeLevels(): string[] {
  const raw = ConfigHelper.instance.config.getOptionalString(
    'app.ownerEmpLevelExclude',
  );
  return parseLevels(raw ?? DEFAULT_EXCLUDE);
}

/**
 * Read warn levels from app config, falling back to defaults.
 * Backed by env var `APP_OWNER_EMPLEVEL_WARN` via `app-config.yaml`.
 *
 * Set env var to `"none"` to disable this tier. Do not use empty string
 * — Backstage rejects empty strings and will throw at runtime.
 */
function getWarnLevels(): string[] {
  const raw = ConfigHelper.instance.config.getOptionalString(
    'app.ownerEmpLevelWarn',
  );
  return parseLevels(raw ?? DEFAULT_WARN);
}

/**
 * Returns `true` if the given level is in the exclude tier.
 * Returns `false` when the exclude list is empty.
 * @param empLevel - Employee level string
 */
export function isExcluded(empLevel: string): boolean {
  const levels = getExcludeLevels();
  if (levels.length === 0) return false;
  return levels.includes(empLevel.toUpperCase());
}

/**
 * Returns `true` if the given level is in the warn tier.
 * Returns `false` when the warn list is empty.
 * @param empLevel - Employee level string
 */
export function isWarnLevel(empLevel: string): boolean {
  const levels = getWarnLevels();
  if (levels.length === 0) return false;
  return levels.includes(empLevel.toUpperCase());
}

/**
 * Fetches the employee level for a given network login via the
 * obiee-data-reader proxy.
 *
 * @param login - Network login ID (e.g. "jsmith001")
 * @returns The raw {@link EmployeeLevelResponse}, or `undefined`
 *          if the user was not found or the request failed.
 */
export async function fetchEmployeeLevel(
  login: string,
): Promise<EmployeeLevelResponse | undefined> {
  try {
    const url = ConfigHelper.instance.proxyUrl(
      `/obiee/getEmployeeLevel/${encodeURIComponent(login)}`,
    ).href;

    const response = await fetch(url);

    if (!response.ok) {
      return undefined;
    }

    const data = (await response.json()) as EmployeeLevelResponse;

    // Empty networkId means "not found" per obiee-data-reader convention
    if (!data.networkId) {
      return undefined;
    }

    return data;
  } catch {
    // Network error, timeout, etc. — fail-open
    return undefined;
  }
}

/**
 * Determines the eligibility tier for a prospective owner.
 *
 * - `'exclude'` — assignment should be blocked
 * - `'warn'`    — confirmation dialog should be shown
 * - `'allow'`   — proceed without dialog
 *
 * Returns `'allow'` when the user is not found in the hierarchy
 * table or when the API call fails (fail-open).
 *
 * @param login - Network login ID of the selected user
 */
export async function checkOwnerEligibility(
  login: string,
): Promise<OwnerEligibilityResult> {
  const data = await fetchEmployeeLevel(login);

  if (!data) {
    return { tier: 'allow' };
  }

  const level = data.empLevel;

  if (isExcluded(level)) {
    return { tier: 'exclude', empLevel: level };
  }

  if (isWarnLevel(level)) {
    return { tier: 'warn', empLevel: level };
  }

  return { tier: 'allow', empLevel: level };
}
